import { useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { UploadCloud, Sparkles } from 'lucide-react';

export default function WorkOrderUploadPage() {
  const [file, setFile] = useState(null);
  const [clientHint, setClientHint] = useState('');
  const [busy, setBusy] = useState(false);
  const [parsing, setParsing] = useState(false);
  const nav = useNavigate();

  const uploadAndParse = async () => {
    if (!file) { toast.error('Choose a file'); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      if (clientHint) form.append('client_hint', clientHint);
      const up = await api.post('/work_orders/upload', form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
      const woId = up.data.id;
      toast.success('Uploaded. Running AI parse…');
      setParsing(true);
      await api.post(`/work_orders/${woId}/parse`, {}, { timeout: 180000 });
      toast.success('Parsed. Please verify the extracted fields.');
      nav(`/work-orders/${woId}/verify`);
    } catch (e) { toast.error(parseErr(e)); }
    finally { setBusy(false); setParsing(false); }
  };

  return (
    <AppShell breadcrumbs={[{ label: 'Work Orders', to: '/work-orders' }, { label: 'Upload' }]}>
      <div className="p-6 max-w-[720px] mx-auto" data-testid="wo-upload-page">
        <h1 className="font-heading text-2xl font-semibold">Upload Work Order</h1>
        <p className="text-[13px] text-[hsl(var(--foreground-muted))]">Supported: PDF, PNG, JPG, WEBP. The AI will extract client, scope, milestones, dates, and terms with per-field confidence — you'll verify before it becomes a project.</p>

        <div className="mt-6 border rounded-xl bg-[hsl(var(--card))] p-6" style={{ borderColor: 'hsl(var(--border))' }}>
          <label className="block">
            <span className="text-[12px] font-medium text-[hsl(var(--foreground-muted))]">File</span>
            <div className="mt-1 border-2 border-dashed rounded-lg p-6 text-center bg-[hsl(var(--surface-1))]" style={{ borderColor: 'hsl(var(--border-strong))' }}>
              <UploadCloud size={20} className="mx-auto text-[hsl(var(--foreground-muted))]" />
              <div className="mt-2 text-[13.5px] text-[hsl(var(--foreground-muted))]">Drop or choose your file</div>
              <input data-testid="wo-upload-file" type="file" accept="application/pdf,image/png,image/jpeg,image/webp" onChange={e => setFile(e.target.files?.[0] || null)} className="mt-3 block mx-auto text-[13px]" />
              {file && <div className="mt-2 text-[12px] font-mono">{file.name} · {(file.size/1024).toFixed(1)} KB</div>}
            </div>
          </label>

          <label className="block mt-4">
            <span className="text-[12px] font-medium text-[hsl(var(--foreground-muted))]">Client hint (optional)</span>
            <Input data-testid="wo-upload-client-hint" value={clientHint} onChange={e => setClientHint(e.target.value)} placeholder="e.g. Aravali Retail Hub" className="mt-1" />
          </label>

          <Button data-testid="wo-upload-submit" disabled={busy || !file} onClick={uploadAndParse} className="mt-4 w-full">
            <Sparkles size={14} className="mr-1" />
            {busy ? (parsing ? 'Parsing with Gemini 2.5 Pro…' : 'Uploading…') : 'Upload & Parse with AI'}
          </Button>
        </div>
      </div>
    </AppShell>
  );
}
